import React, { useState, useEffect } from 'react';
import {
    Card,
    Button,
    Space,
    Tag,
    message,
    Table,
    Empty,
    Select,
    Modal,
    Popconfirm,
    Typography
} from 'antd';
import {
    SyncOutlined,
    ReloadOutlined,
    CheckCircleOutlined,
    CloseCircleOutlined,
    ThunderboltOutlined,
    CloudSyncOutlined,
    EyeOutlined,
    StopOutlined,
    ExperimentOutlined,
    PauseCircleOutlined
} from '@ant-design/icons';
import cdnService from '../../services/cdnService';
import cdnDnsOpsService, { describeSyncResult } from '../../services/cdnDnsOpsService';
import DnsSyncPlanView from './DnsSyncPlanView';

const { Option } = Select;
const { Text } = Typography;

const STATUS_TAG = {
    success: { color: 'success', icon: <CheckCircleOutlined />, text: '已应用' },
    dryrun: { color: 'processing', icon: <ExperimentOutlined />, text: '演练' },
    guarded: { color: 'warning', icon: <StopOutlined />, text: '护栏拦截' },
    skipped: { color: 'default', icon: <PauseCircleOutlined />, text: '跳过' },
    failed: { color: 'error', icon: <CloseCircleOutlined />, text: '失败' },
};

const toast = ({ type, text }) => message[type](text, type === 'success' ? 3 : 6);

const parseJson = (v, fallback) => {
    if (v && typeof v === 'object') return v;
    try { return JSON.parse(v || ''); } catch { return fallback; }
};

const fmtAge = (s) => {
    if (s === null || s === undefined) return '从未';
    if (s < 120) return `${s} 秒前`;
    if (s < 7200) return `${Math.round(s / 60)} 分钟前`;
    return `${Math.round(s / 3600)} 小时前`;
};

const CdnDnsSync = () => {
    const [groups, setGroups] = useState([]);
    const [selectedGroupId, setSelectedGroupId] = useState(null);
    const [syncLogs, setSyncLogs] = useState([]);
    const [liveness, setLiveness] = useState(null);
    const [loading, setLoading] = useState(false);
    const [syncing, setSyncing] = useState(false);
    const [previewing, setPreviewing] = useState(false);
    const [plan, setPlan] = useState(null);

    useEffect(() => {
        loadGroups();
    }, []);

    useEffect(() => {
        loadSyncStatus(selectedGroupId);
    }, [selectedGroupId]);

    const loadGroups = async () => {
        const response = await cdnService.getCdnGroups(null, 'active');
        if (response.success) {
            setGroups(response.data || []);
        } else {
            message.error(response.error || '加载分组失败');
        }
    };

    const loadSyncStatus = async (groupId = selectedGroupId) => {
        setLoading(true);
        const response = await cdnDnsOpsService.getStatus(groupId);
        if (response.success) {
            // 后端返回顶层 logs / liveness
            setSyncLogs(Array.isArray(response.logs) ? response.logs : Object.values(response.logs || {}));
            setLiveness(response.liveness || null);
        } else {
            message.error(response.error || '加载同步日志失败');
        }
        setLoading(false);
    };

    const handlePreview = async () => {
        if (!selectedGroupId) return;
        setPreviewing(true);
        const response = await cdnDnsOpsService.previewGroup(selectedGroupId);
        setPreviewing(false);
        if (response.success) {
            setPlan(response);
        } else {
            message.error(response.error || '预览失败');
        }
    };

    const handleSyncGroup = async () => {
        if (!selectedGroupId) return;
        setSyncing(true);
        const response = await cdnDnsOpsService.syncGroup(selectedGroupId);
        setSyncing(false);
        toast(describeSyncResult(response));
        setPlan(null);
        loadSyncStatus();
    };

    const handleSyncAll = async () => {
        setSyncing(true);
        const response = await cdnDnsOpsService.syncAll();
        setSyncing(false);
        if (!response.success) {
            message.error(response.error || '同步失败');
            return;
        }
        const results = response.results || [];
        if (!results.length) {
            message.info('没有可同步的分组');
        }
        results.forEach((g) => {
            const d = describeSyncResult(g.result);
            toast({ type: d.type, text: `${g.group_name || '#' + g.group_id}：${d.text}` });
        });
        loadSyncStatus();
    };

    const columns = [
        { title: 'ID', dataIndex: 'id', key: 'id', width: 70 },
        {
            title: '分组',
            dataIndex: 'group_name',
            key: 'group_name',
            render: (name, r) => name || (r.group_id ? `#${r.group_id}` : '-')
        },
        {
            title: '状态',
            dataIndex: 'status',
            key: 'status',
            width: 110,
            render: (status) => {
                const c = STATUS_TAG[status] || { color: 'default', text: status || '-' };
                return <Tag icon={c.icon} color={c.color}>{c.text}</Tag>;
            }
        },
        {
            title: '变更',
            key: 'changes',
            width: 160,
            render: (_, r) => {
                const detail = parseJson(r.detail, null);
                if (r.status === 'dryrun' && detail) {
                    return <Text type="secondary">计划 +{detail.add?.length || 0} / -{detail.remove?.length || 0}</Text>;
                }
                return (
                    <Space size={4}>
                        <Text type="success">+{r.records_added || 0}</Text>
                        <Text type="danger">-{r.records_deleted || 0}</Text>
                        {Number(r.records_updated) > 0 && <Text type="warning">~{r.records_updated}</Text>}
                    </Space>
                );
            }
        },
        {
            title: '发布节点',
            dataIndex: 'active_nodes',
            key: 'active_nodes',
            width: 100,
            render: (nodes) => {
                const list = parseJson(nodes, null);
                return Array.isArray(list) ? <Tag color="green">{list.length} 个</Tag> : '-';
            }
        },
        {
            title: '耗时',
            dataIndex: 'duration_ms',
            key: 'duration_ms',
            width: 90,
            render: (ms) => (ms !== null && ms !== undefined ? `${ms}ms` : '-')
        },
        {
            title: '时间',
            dataIndex: 'created_at',
            key: 'created_at',
            width: 180,
            render: (text) => (text ? new Date(text.replace(' ', 'T')).toLocaleString('zh-CN') : '-')
        },
        {
            title: '原因',
            dataIndex: 'error_message',
            key: 'error_message',
            ellipsis: true,
            render: (err) => (err ? <Text type="warning">{err}</Text> : '-')
        }
    ];

    const expandedRowRender = (r) => {
        const detail = parseJson(r.detail, null);
        const nodes = parseJson(r.active_nodes, []);
        return (
            <Space direction="vertical" size={4} style={{ padding: '4px 24px' }}>
                {detail?.summary && <Text code>{detail.record} · {detail.summary}</Text>}
                {(detail?.add || []).map((a) => <Text key={'a' + a} type="success">+ {a}</Text>)}
                {(detail?.remove || []).map((x) => <Text key={'r' + x} type="danger">- {x}</Text>)}
                {(detail?.notes || []).map((n, i) => <Text key={'n' + i} type="secondary">{n}</Text>)}
                <div>
                    <Text strong>发布节点：</Text>
                    {Array.isArray(nodes) && nodes.length
                        ? nodes.map((id) => <Tag key={id}>#{id}</Tag>)
                        : <Text type="secondary">无</Text>}
                </div>
                {r.error_message && <Text type="warning">{r.error_message}</Text>}
            </Space>
        );
    };

    return (
        <Card
            title={<Space><CloudSyncOutlined />CDN 端点 DNS 同步</Space>}
            extra={
                <Space>
                    <Select
                        style={{ width: 200 }}
                        placeholder="全部分组"
                        allowClear
                        value={selectedGroupId}
                        onChange={(v) => setSelectedGroupId(v ?? null)}
                    >
                        {groups.map((group) => (
                            <Option key={group.id} value={group.id}>{group.name}</Option>
                        ))}
                    </Select>
                    <Button icon={<ReloadOutlined />} onClick={() => loadSyncStatus()}>刷新</Button>
                </Space>
            }
        >
            <Space wrap style={{ marginBottom: 16 }}>
                {liveness && (
                    <>
                        <Tag color={liveness.mode === 'apply' ? 'green' : 'blue'}>模式 {liveness.mode}</Tag>
                        <Tag color={liveness.stale ? 'red' : 'default'}>
                            调度器上次运行 {fmtAge(liveness.age_seconds)}{liveness.stale ? '（已停摆）' : ''}
                        </Tag>
                    </>
                )}
                <Text type="secondary">调度器约每 60 秒自动同步；此处可预览或立即执行。</Text>
            </Space>

            <Space wrap style={{ marginBottom: 16 }}>
                <Button
                    icon={<EyeOutlined />}
                    onClick={handlePreview}
                    loading={previewing}
                    disabled={!selectedGroupId}
                >
                    预览变更
                </Button>
                <Popconfirm
                    title="立即同步选中分组？"
                    description="将按预览中的变更写入 DNS"
                    onConfirm={handleSyncGroup}
                    disabled={!selectedGroupId || syncing}
                >
                    <Button
                        type="primary"
                        icon={<SyncOutlined />}
                        loading={syncing}
                        disabled={!selectedGroupId}
                    >
                        立即同步选中分组
                    </Button>
                </Popconfirm>
                <Popconfirm
                    title="立即同步所有分组？"
                    description="含清理失效节点记录（受下限护栏保护）"
                    onConfirm={handleSyncAll}
                    disabled={syncing}
                >
                    <Button icon={<ThunderboltOutlined />} loading={syncing}>
                        同步所有分组
                    </Button>
                </Popconfirm>
            </Space>

            <Table
                columns={columns}
                dataSource={syncLogs}
                rowKey="id"
                loading={loading}
                size="middle"
                pagination={{ pageSize: 20, showSizeChanger: true, showTotal: (t) => `最近 ${t} 条` }}
                scroll={{ x: 'max-content' }}
                locale={{ emptyText: <Empty description="暂无同步记录" /> }}
                expandable={{ expandedRowRender }}
            />

            <Modal
                title="预览变更"
                open={!!plan}
                onCancel={() => setPlan(null)}
                width={760}
                footer={[
                    <Button key="close" onClick={() => setPlan(null)}>关闭</Button>,
                    <Popconfirm
                        key="apply"
                        title="按此预览立即应用？"
                        onConfirm={handleSyncGroup}
                        disabled={!plan?.has_changes}
                    >
                        <Button type="primary" loading={syncing} disabled={!plan?.has_changes}>应用</Button>
                    </Popconfirm>
                ]}
            >
                <DnsSyncPlanView plan={plan} />
            </Modal>
        </Card>
    );
};

export default CdnDnsSync;
